import React, { useState, useEffect, useRef } from 'react';
import { settingsAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

// Fallback default sound (served from the frontend's public/ folder) used for the
// "Test default" preview when no custom sound is uploaded. Matches the backend's
// reported defaultSoundUrl.
const DEFAULT_SOUND_URL = '/notification-sound.mp3';

const NotificationSoundSettings = () => {
  const [settings, setSettings] = useState({
    restaurantOrderSound: '',
    deliveryNotificationSound: '',
    customSoundEnabled: false
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({ restaurant: false, delivery: false });
  const [deleting, setDeleting] = useState({ restaurant: false, delivery: false });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [playingSound, setPlayingSound] = useState(null);

  const restaurantFileRef = useRef(null);
  const deliveryFileRef = useRef(null);
  const audioRef = useRef(null);

  const theme = {
    primaryColor: '#FF6961',
    primaryDark: '#E6524A',
    textPrimary: '#0F172A',
    textSecondary: '#64748B',
    backgroundColor: '#F8FAFC'
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getNotificationSoundSettings();
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch notification sound settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCustomSound = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await settingsAPI.updateNotificationSoundSettings({
        customSoundEnabled: !settings.customSoundEnabled
      });

      if (response.data.success) {
        setSettings(prev => ({ ...prev, customSoundEnabled: !prev.customSoundEnabled }));
        setMessage({
          type: 'success',
          text: `Custom notification sounds ${!settings.customSoundEnabled ? 'enabled' : 'disabled'}`
        });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update setting' });
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (type, file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Invalid file type. Please upload MP3 or WAV files only.' });
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File too large. Maximum size is 2MB.' });
      return;
    }

    setUploading(prev => ({ ...prev, [type]: true }));
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('sound', file);
      formData.append('type', type);

      const response = await settingsAPI.uploadNotificationSound(formData);

      if (response.data.success) {
        await fetchSettings();
        setMessage({ type: 'success', text: `${type === 'restaurant' ? 'Restaurant order' : 'Delivery'} notification sound uploaded successfully!` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to upload sound file' });
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
      // Reset file input
      if (type === 'restaurant' && restaurantFileRef.current) {
        restaurantFileRef.current.value = '';
      } else if (deliveryFileRef.current) {
        deliveryFileRef.current.value = '';
      }
    }
  };

  const handleDeleteSound = async (type) => {
    if (!window.confirm(`Are you sure you want to delete the ${type === 'restaurant' ? 'restaurant order' : 'delivery'} notification sound?`)) {
      return;
    }

    setDeleting(prev => ({ ...prev, [type]: true }));
    setMessage({ type: '', text: '' });

    try {
      const response = await settingsAPI.deleteNotificationSound(type);

      if (response.data.success) {
        await fetchSettings();
        setMessage({ type: 'success', text: `${type === 'restaurant' ? 'Restaurant order' : 'Delivery'} notification sound deleted.` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to delete sound file' });
    } finally {
      setDeleting(prev => ({ ...prev, [type]: false }));
    }
  };

  const playSound = (url, type) => {
    if (!url) return;

    if (playingSound === type) {
      // Stop current sound
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlayingSound(null);
    } else {
      // Play new sound
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.play().catch(err => {
          console.log('Could not play sound:', err);
          setMessage({ type: 'error', text: 'Could not play sound. Please check your browser settings.' });
        });
        setPlayingSound(type);
      }
    }
  };

  const handleAudioEnded = () => {
    setPlayingSound(null);
  };

  if (loading) {
    return (
      <div style={{ background: theme.backgroundColor, minHeight: '100vh' }}>
        <Navbar />
        <div style={{ padding: '40px 24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '16px' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  // What the "Test" button plays: the custom sound only when it's both enabled AND
  // uploaded (that's what restaurants actually hear); otherwise the default sound.
  const defaultSoundUrl = settings.defaultSoundUrl || DEFAULT_SOUND_URL;
  const activeRestaurantSoundUrl =
    settings.customSoundEnabled && settings.restaurantOrderSound
      ? settings.restaurantOrderSound
      : defaultSoundUrl;
  const activeDeliverySoundUrl =
    settings.customSoundEnabled && settings.deliveryNotificationSound
      ? settings.deliveryNotificationSound
      : defaultSoundUrl;

  return (
    <div style={{ background: theme.backgroundColor, minHeight: '100vh' }}>
      <Navbar />
      <audio ref={audioRef} onEnded={handleAudioEnded} />

      <div style={{ padding: '40px 24px', maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: theme.textPrimary,
            marginBottom: '8px'
          }}>
            Notification Sound Settings
          </h1>
          <p style={{ color: theme.textSecondary, fontSize: '16px' }}>
            Customize the notification sounds for restaurant order alerts and delivery notifications.
          </p>
        </div>

        {/* Message */}
        {message.text && (
          <div style={{
            padding: '16px 20px',
            borderRadius: '12px',
            marginBottom: '24px',
            background: message.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${message.type === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            color: message.type === 'success' ? '#059669' : '#DC2626',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '20px' }}>{message.type === 'success' ? '✅' : '❌'}</span>
            <span style={{ fontWeight: '500' }}>{message.text}</span>
          </div>
        )}

        {/* Enable/Disable Toggle */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: theme.textPrimary, marginBottom: '4px' }}>
                Enable Custom Notification Sounds
              </h3>
              <p style={{ color: theme.textSecondary, fontSize: '14px' }}>
                When enabled, restaurants will hear your custom sound instead of the default notification.
              </p>
            </div>
            <button
              onClick={handleToggleCustomSound}
              disabled={saving}
              style={{
                width: '60px',
                height: '32px',
                borderRadius: '16px',
                border: 'none',
                background: settings.customSoundEnabled ? theme.primaryColor : '#E2E8F0',
                cursor: saving ? 'wait' : 'pointer',
                position: 'relative',
                transition: 'background 0.3s'
              }}
            >
              <div style={{
                width: '26px',
                height: '26px',
                borderRadius: '13px',
                background: 'white',
                position: 'absolute',
                top: '3px',
                left: settings.customSoundEnabled ? '31px' : '3px',
                transition: 'left 0.3s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }} />
            </button>
          </div>
        </div>

        {/* Restaurant Order Sound */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'rgba(255, 105, 97, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px'
            }}>
              📦
            </div>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: theme.textPrimary, marginBottom: '4px' }}>
                Restaurant Order Sound
              </h3>
              <p style={{ color: theme.textSecondary, fontSize: '14px' }}>
                Plays when a new order arrives at the restaurant dashboard.
              </p>
            </div>
            <button
              onClick={() => playSound(activeRestaurantSoundUrl, 'restaurant-test')}
              title="Hear exactly what restaurants will get for a new order"
              style={{
                marginLeft: 'auto',
                padding: '10px 16px',
                borderRadius: '10px',
                border: 'none',
                background: playingSound === 'restaurant-test' ? theme.primaryDark : theme.primaryColor,
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              {playingSound === 'restaurant-test'
                ? '⏹ Stop'
                : `🔊 Test ${settings.customSoundEnabled && settings.restaurantOrderSound ? 'sound' : 'default'}`}
            </button>
          </div>

          {settings.restaurantOrderSound ? (
            <div style={{
              background: '#F8FAFC',
              borderRadius: '12px',
              padding: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '20px' }}>🔊</span>
                <span style={{ color: theme.textPrimary, fontWeight: '500' }}>Custom sound uploaded</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => playSound(settings.restaurantOrderSound, 'restaurant')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: playingSound === 'restaurant' ? theme.primaryDark : theme.primaryColor,
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {playingSound === 'restaurant' ? '⏹ Stop' : '▶ Play'}
                </button>
                <button
                  onClick={() => handleDeleteSound('restaurant')}
                  disabled={deleting.restaurant}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid #EF4444',
                    background: 'white',
                    color: '#EF4444',
                    cursor: deleting.restaurant ? 'wait' : 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {deleting.restaurant ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{
              border: '2px dashed #E2E8F0',
              borderRadius: '12px',
              padding: '32px',
              textAlign: 'center'
            }}>
              <input
                ref={restaurantFileRef}
                type="file"
                accept="audio/mpeg,audio/mp3,audio/wav"
                onChange={(e) => handleFileUpload('restaurant', e.target.files[0])}
                style={{ display: 'none' }}
              />
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎵</div>
              <p style={{ color: theme.textSecondary, marginBottom: '16px' }}>
                Upload MP3 or WAV file (max 2MB)
              </p>
              <button
                onClick={() => restaurantFileRef.current?.click()}
                disabled={uploading.restaurant}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: theme.primaryColor,
                  color: 'white',
                  cursor: uploading.restaurant ? 'wait' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                {uploading.restaurant ? 'Uploading...' : 'Upload Sound'}
              </button>
            </div>
          )}
        </div>

        {/* Delivery Notification Sound */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'rgba(59, 130, 246, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px'
            }}>
              🛵
            </div>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: theme.textPrimary, marginBottom: '4px' }}>
                Delivery Notification Sound
              </h3>
              <p style={{ color: theme.textSecondary, fontSize: '14px' }}>
                Plays when a new delivery request arrives for delivery partners.
              </p>
            </div>
            <button
              onClick={() => playSound(activeDeliverySoundUrl, 'delivery-test')}
              title="Preview the delivery alert sound"
              style={{
                marginLeft: 'auto',
                padding: '10px 16px',
                borderRadius: '10px',
                border: 'none',
                background: playingSound === 'delivery-test' ? '#2563EB' : '#3B82F6',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              {playingSound === 'delivery-test'
                ? '⏹ Stop'
                : `🔊 Test ${settings.customSoundEnabled && settings.deliveryNotificationSound ? 'sound' : 'default'}`}
            </button>
          </div>

          {settings.deliveryNotificationSound ? (
            <div style={{
              background: '#F8FAFC',
              borderRadius: '12px',
              padding: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '20px' }}>🔊</span>
                <span style={{ color: theme.textPrimary, fontWeight: '500' }}>Custom sound uploaded</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => playSound(settings.deliveryNotificationSound, 'delivery')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: playingSound === 'delivery' ? '#2563EB' : '#3B82F6',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {playingSound === 'delivery' ? '⏹ Stop' : '▶ Play'}
                </button>
                <button
                  onClick={() => handleDeleteSound('delivery')}
                  disabled={deleting.delivery}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid #EF4444',
                    background: 'white',
                    color: '#EF4444',
                    cursor: deleting.delivery ? 'wait' : 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {deleting.delivery ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{
              border: '2px dashed #E2E8F0',
              borderRadius: '12px',
              padding: '32px',
              textAlign: 'center'
            }}>
              <input
                ref={deliveryFileRef}
                type="file"
                accept="audio/mpeg,audio/mp3,audio/wav"
                onChange={(e) => handleFileUpload('delivery', e.target.files[0])}
                style={{ display: 'none' }}
              />
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎵</div>
              <p style={{ color: theme.textSecondary, marginBottom: '16px' }}>
                Upload MP3 or WAV file (max 2MB)
              </p>
              <button
                onClick={() => deliveryFileRef.current?.click()}
                disabled={uploading.delivery}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#3B82F6',
                  color: 'white',
                  cursor: uploading.delivery ? 'wait' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                {uploading.delivery ? 'Uploading...' : 'Upload Sound'}
              </button>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div style={{
          background: 'rgba(59, 130, 246, 0.05)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1E40AF', marginBottom: '12px' }}>
            ℹ️ Important Information
          </h3>
          <ul style={{ margin: 0, paddingLeft: '20px', color: theme.textSecondary, lineHeight: '1.8' }}>
            <li>Supported formats: MP3 and WAV files only</li>
            <li>Maximum file size: 2MB per sound file</li>
            <li>Recommended duration: 1-5 seconds for best user experience</li>
            <li>Restaurant order sound plays on the restaurant dashboard when new orders arrive</li>
            <li>Delivery notification sound is for delivery partner apps (future feature)</li>
            <li>When custom sounds are disabled, the system uses the default notification sound</li>
            <li>Sound files are stored securely in cloud storage (R2/S3)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default NotificationSoundSettings;
